import { useEffect, useState } from 'react';
import { DateTime } from 'luxon';
import { Plus, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Switch } from '@/components/settings/settings-controls';
import { TimeField } from '@/components/pages/work-plan/planner-bits';
import {
  LABEL_MAX,
  MAX_WINDOWS_PER_DAY,
  MESSAGE_MAX,
  findUnknownPlaceholders,
  validateWindows,
} from '@/lib/bot-service-hours';
import { NumberMultiSelect } from './number-multi-select';
import { PlaceholderChips } from './out-of-hours-message-editor';

// Add/edit dialog for one special day (a date exception: closed all day, or replacement
// windows for that date). Validates the server's rules before enabling the primary button;
// saving hands the parent a complete day object to upsert into the DRAFT (the save bar
// commits it, like every other draft edit).

const FieldLabel = ({ children }) => (
  <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
    {children}
  </span>
);

export function SpecialDayDialog({ open, onOpenChange, day, gateways, placeholders, onSave }) {
  const [label, setLabel] = useState('');
  const [date, setDate] = useState(null); // JS Date for the DatePicker
  const [gatewayBotIds, setGatewayBotIds] = useState(null);
  const [closed, setClosed] = useState(true);
  const [windows, setWindows] = useState([{ start: '09:00', end: '12:00' }]);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!open) return;
    setLabel(day?.label || '');
    setDate(day?.date ? DateTime.fromISO(day.date).toJSDate() : null);
    setGatewayBotIds(day?.gatewayBotIds ?? null);
    setClosed(day ? Boolean(day.closed) : true);
    setWindows(
      day && !day.closed && Array.isArray(day.windows) && day.windows.length > 0
        ? day.windows.map((window) => ({ ...window }))
        : [{ start: '09:00', end: '12:00' }],
    );
    setMessage(day?.message || '');
  }, [open, day]);

  const dateStr = date ? DateTime.fromJSDate(date).toFormat('yyyy-LL-dd') : '';
  const labelLength = Array.from(label.trim()).length;
  const labelError =
    labelLength === 0 ? 'Give the day a label.' : labelLength > LABEL_MAX ? `At most ${LABEL_MAX} characters.` : '';
  const windowsError = closed
    ? ''
    : windows.length === 0
      ? 'Add at least one time window.'
      : validateWindows(windows);
  const unknownTokens = findUnknownPlaceholders(message, placeholders);
  const messageError =
    Array.from(message).length > MESSAGE_MAX
      ? `At most ${MESSAGE_MAX} characters.`
      : unknownTokens.length > 0
        ? `Unknown placeholder ${unknownTokens.map((t) => `{{${t}}}`).join(', ')}.`
        : '';
  const scopeError =
    Array.isArray(gatewayBotIds) && gatewayBotIds.length === 0 ? 'Select at least one number.' : '';
  const canSave = !labelError && Boolean(dateStr) && !windowsError && !messageError && !scopeError;

  const handleSave = () => {
    if (!canSave) return;
    onSave({
      id: day?.id || '',
      label: label.trim(),
      gatewayBotIds,
      date: dateStr,
      closed,
      windows: closed ? [] : windows,
      message: message.trim() ? message : null,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{day ? 'Edit special day' : 'Add special day'}</DialogTitle>
          <DialogDescription>
            A date exception on top of the weekly hours — a renovation closure, a holiday
            half-day. Saved with the rest of your changes.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3.5">
          <label className="flex flex-col gap-1">
            <FieldLabel>Label</FieldLabel>
            <Input
              value={label}
              maxLength={LABEL_MAX}
              placeholder="e.g. Renovation day"
              onChange={(event) => setLabel(event.target.value)}
            />
            {label && labelError ? <p className="text-[11px] text-destructive">{labelError}</p> : null}
          </label>

          <div className="flex flex-wrap gap-3">
            <div className="flex w-44 flex-col gap-1">
              <FieldLabel>Date</FieldLabel>
              <DatePicker date={date} setDate={setDate} className="h-8" placeholder="Pick a date" />
            </div>
            <div className="flex flex-col gap-1">
              <FieldLabel>Applies to</FieldLabel>
              <NumberMultiSelect
                gateways={gateways}
                value={gatewayBotIds}
                onChange={setGatewayBotIds}
              />
              {scopeError ? <p className="text-[11px] text-destructive">{scopeError}</p> : null}
            </div>
          </div>

          <div className="flex items-center gap-2.5">
            <Switch checked={closed} label="Closed all day" onChange={setClosed} />
            <span className="text-sm text-foreground">Closed all day</span>
          </div>

          {!closed ? (
            <div className="flex flex-col gap-1.5 rounded-lg border border-border bg-muted/20 p-2.5">
              <FieldLabel>Open hours on this date</FieldLabel>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
                {windows.map((window, index) => (
                  <div key={index} className="flex items-center gap-1.5">
                    <div className="w-[6.5rem]">
                      <TimeField
                        value={window.start}
                        aria-label={`Window ${index + 1} start`}
                        onChange={(event) =>
                          setWindows((current) =>
                            current.map((w, i) => (i === index ? { ...w, start: event.target.value } : w)),
                          )
                        }
                        className={cn(windowsError && 'border-destructive')}
                      />
                    </div>
                    <span className="text-xs text-muted-foreground">–</span>
                    <div className="w-[6.5rem]">
                      <TimeField
                        value={window.end}
                        aria-label={`Window ${index + 1} end`}
                        onChange={(event) =>
                          setWindows((current) =>
                            current.map((w, i) => (i === index ? { ...w, end: event.target.value } : w)),
                          )
                        }
                        className={cn(windowsError && 'border-destructive')}
                      />
                    </div>
                    {windows.length > 1 ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="size-7"
                        aria-label={`Remove window ${index + 1}`}
                        onClick={() => setWindows((current) => current.filter((_, i) => i !== index))}
                      >
                        <Trash2 className="size-3.5 text-muted-foreground" aria-hidden />
                      </Button>
                    ) : null}
                  </div>
                ))}
                {windows.length < MAX_WINDOWS_PER_DAY ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs text-muted-foreground"
                    onClick={() => setWindows((current) => [...current, { start: '14:00', end: '17:00' }])}
                  >
                    <Plus className="mr-1 size-3.5" aria-hidden />
                    Add hours
                  </Button>
                ) : null}
              </div>
              {windowsError ? <p className="text-[11px] text-destructive">{windowsError}</p> : null}
            </div>
          ) : null}

          <div className="flex flex-col gap-1.5">
            <FieldLabel>Message for this day (optional)</FieldLabel>
            <Textarea
              value={message}
              rows={3}
              placeholder="Leave empty to use the regular out-of-hours message."
              onChange={(event) => setMessage(event.target.value)}
              className={cn('font-mono text-sm', messageError && 'border-destructive')}
            />
            <PlaceholderChips placeholders={placeholders} />
            {messageError ? <p className="text-[11px] text-destructive">{messageError}</p> : null}
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSave} disabled={!canSave}>
            {day ? 'Save day' : 'Add day'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
